import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { BirthDetails, AstrolabeData, SageState } from './types';
import { generateAstrolabe } from './services/ziweiService';
import { streamInterpretation } from './services/geminiService';
import WiseOldMan from './components/WiseOldMan';
import InputForm from './components/InputForm';
import AstrolabeView from './components/AstrolabeView';
import { useAuth } from './src/hooks/useAuth';
import { AuthModal } from './src/components/AuthModal';
import PricingModal from './src/components/PricingModal';
import FollowupInput from './src/components/FollowupInput';
import AdSlot from './src/components/AdSlot';

const App: React.FC = () => {
  const [step, setStep] = useState<'input' | 'result'>('input');
  const [birthDetails, setBirthDetails] = useState<BirthDetails | null>(null);
  const [astrolabeData, setAstrolabeData] = useState<AstrolabeData | null>(null);
  const [interpretation, setInterpretation] = useState<string>('');
  const [sageState, setSageState] = useState<SageState>(SageState.IDLE);
  const [error, setError] = useState<string | null>(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [readingId, setReadingId] = useState<string | null>(null);

  const { user, loading: authLoading, logout } = useAuth();
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto scroll during streaming
  useEffect(() => {
    if (sageState === SageState.TALKING) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [interpretation, sageState]);

  const handleInputInteraction = () => {
    if (sageState === SageState.IDLE) {
      setSageState(SageState.READING);
      // Reset to IDLE after a short delay if no more interaction
      setTimeout(() => {
        setSageState(prev => prev === SageState.READING ? SageState.IDLE : prev);
      }, 1500);
    }
  };

  const handleFormSubmit = async (details: BirthDetails) => {
    setBirthDetails(details);
    setSageState(SageState.THINKING);
    setError(null);
    setStep('result');

    try {
      // 1. Generate Chart locally
      const data = generateAstrolabe(details);
      setAstrolabeData(data);

      // 2. Call API and stream the response
      const result = await streamInterpretation(data, details, null);

      // Capture reading ID for follow-up questions (paid users)
      if (result.readingId) {
        setReadingId(result.readingId);
      }

      // Display streaming response incrementally
      let fullText = '';
      let isFirstChunk = true;

      for await (const chunk of result.stream) {
        fullText += chunk;
        setInterpretation(fullText);

        // Start talking animation when first chunk arrives
        if (isFirstChunk) {
          setSageState(SageState.TALKING);
          isFirstChunk = false;
        }
      }

      // Stop talking after streaming completes
      setTimeout(() => {
        setSageState(SageState.IDLE);
      }, 3000);

    } catch (e) {
      console.error(e);
      setError("大師正在閉關修煉 (API Error)，請稍後再試。");
      setSageState(SageState.IDLE);
    }
  };

  const reset = () => {
    setStep('input');
    setInterpretation('');
    setAstrolabeData(null);
    setError(null);
    setSageState(SageState.IDLE);
    setReadingId(null);
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 pb-12">
      {/* Header */}
      <header className="py-6 border-b border-slate-800 bg-slate-900/50 backdrop-blur sticky top-0 z-10">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <h1 className="text-2xl font-serif text-amber-500 tracking-widest flex items-center gap-2">
            <span className="text-3xl">🔮</span> 紫微斗數・智者
          </h1>
          <div className="flex items-center gap-3">
            {step === 'result' && (
              <button
                onClick={reset}
                className="text-xs border border-slate-600 px-3 py-1 rounded hover:bg-slate-800 transition"
              >
                重新排盤
              </button>
            )}
            {!authLoading && (
              user ? (
                <div className="flex items-center gap-2">
                  {user.tier === 'paid' && user.quota > 0 && (
                    <span className="text-xs text-amber-400">
                      剩餘 {user.quota} 次
                    </span>
                  )}
                  {user.tier !== 'paid' && (
                    <button
                      onClick={() => setShowPricingModal(true)}
                      className="text-xs border border-amber-600 text-amber-500 px-3 py-1 rounded hover:bg-amber-600/20 transition"
                    >
                      升級
                    </button>
                  )}
                  <span className="text-xs text-slate-400 hidden sm:inline">{user.email}</span>
                  <button
                    onClick={logout}
                    className="text-xs text-slate-400 hover:text-amber-500 transition"
                  >
                    登出
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowAuthModal(true)}
                  className="text-xs border border-amber-600 text-amber-500 px-3 py-1 rounded hover:bg-amber-600/20 transition"
                >
                  登入
                </button>
              )
            )}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">

        {/* The Sage Section */}
        <section className="flex flex-col items-center justify-center mb-8">
          <WiseOldMan state={sageState} />
          <div className={`mt-4 p-4 rounded-xl max-w-2xl text-center transition-all duration-500 ${step === 'input' ? 'bg-transparent' : 'bg-slate-900 border border-slate-700'}`}>
            {step === 'input' ? (
              <p className="text-slate-400 font-serif text-lg">
                "年輕人，告訴我你的生辰八字，老夫為你指點迷津..."
              </p>
            ) : (
              <div className="text-left">
                {error ? (
                  <p className="text-red-400 text-center">{error}</p>
                ) : interpretation ? (
                  <div className="prose prose-invert prose-amber max-w-none text-sm md:text-base leading-relaxed">
                    <ReactMarkdown>{interpretation}</ReactMarkdown>
                    <div ref={bottomRef} />
                  </div>
                ) : (
                  <p className="text-slate-500 text-center italic animate-pulse">
                    大師正在掐指一算...
                  </p>
                )}
              </div>
            )}
          </div>
        </section>

        {/* Dynamic Content Area */}
        <div className="transition-all duration-500">
          {step === 'input' && (
            <div className="animate-fade-in-up">
              <InputForm
                onSubmit={handleFormSubmit}
                onInteraction={handleInputInteraction}
                isLoading={sageState === SageState.THINKING}
              />
            </div>
          )}

          {step === 'result' && user?.tier === 'paid' && readingId && (
            <FollowupInput
              readingId={readingId}
              onResponse={(text) => {
                setInterpretation(prev => prev + '\n\n---\n\n**追問回覆：**\n\n' + text);
              }}
            />
          )}

          {step === 'result' && interpretation && (
            <AdSlot
              isPaid={user?.tier === 'paid'}
              onUpgradeClick={() => setShowPricingModal(true)}
            />
          )}

          {step === 'result' && astrolabeData && (
            <div className="animate-fade-in mt-12 border-t border-slate-800 pt-12">
              <h3 className="text-center text-slate-500 mb-6 font-serif uppercase tracking-widest text-sm">命盤結構</h3>
              <AstrolabeView data={astrolabeData} />
            </div>
          )}
        </div>

      </main>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />

      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
      />
    </div>
  );
};

export default App;
